// DynamicSeo.js
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import axios from "axios";

const DynamicSeo = () => {
  const location = useLocation();
  const [seoData, setSeoData] = useState(null);
  const server_url = localStorage.getItem("server_url") || "";

  // Extract page name from path
  const getPageName = (pathname) => {
    if (pathname === "/") return "home";

    // Remove leading slash and any query parameters
    const path = pathname.replace(/^\//, "").split("?")[0];

    // Map specific routes to their page names
    const routeMap = {
      AddProduct: "AddProduct",
      MyOrderds: "MyOrderds",
      OrderDetails: "OrderDetails",
      Mycart: "Mycart",
      MyDevice: "MyDevice",
      More: "More",
      MyTransaction: "MyTransaction",
      Complain: "Complain",
      UpdateProdile: "UpdateProdile",
      UpdateBankDetails: "UpdateBankDetails",
      Login: "Login",
      registration: "registration",
    };

    // Check for routes with parameters (like /ProductDetails/:id/:name)
    if (path.startsWith("ProductDetails/")) {
      return null; // Return null for ProductDetail page - it has its own SEO
    }

    return routeMap[path] || path;
  };

  useEffect(() => {
    const fetchSeoData = async () => {
      try {
        const pageName = getPageName(location.pathname);

        // Don't fetch for ProductDetail page or if no page name
        if (!pageName || location.pathname.startsWith("/ProductDetails")) {
          setSeoData(null);
          return;
        }

        const formData = new FormData();
        formData.append("company_id", "2");

        const response = await axios.post(
          `${server_url}static_seo.php`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );

        if (response.data.status === "1" && response.data.result) {
          // Find SEO data for current page
          const pageSeo = response.data.result.find(
            (item) =>
              item.seo_content_page.toLowerCase() === pageName.toLowerCase(),
          );

          if (pageSeo) {
            setSeoData(pageSeo);
          }
        }
      } catch (error) {
        console.error("Error fetching SEO data:", error);
        setSeoData(null);
      }
    };

    fetchSeoData();
  }, [location.pathname, server_url]);

  if (!seoData) return null;

  return (
    <Helmet>
      <meta name="Robots" content="all" />
      {/* Primary Meta Tags */}
      <meta name="Googlebot" content="index, follow" />
      <meta name="Yahoobot" content="Index, Follow" />
      <meta name="MSNbot" content="index, follow" />
      <meta name="allow-search" content="yes" />
      <meta name="robots" content="Index, Follow" />
      <meta name="rating" content="General" />
      <meta name="author" content="Recycling Hub" />
      <meta name="language" content="EN" />
      <meta name="copyright" content="https://www.recyclinghub.in/" />
      <meta name="reply-to" content="info@recyclinghub.in" />
      <meta name="document-type" content="Public" />
      <meta name="publisher" content="recyclinghub.in" />
      <meta name="document-rating" content="Safe for Kids" />
      <meta name="geo.region" content="IN-GJ" />
      <meta name="document-distribution" content="Global" />

      <meta
        name="google-site-verification"
        content="eKih2tGPA22Ji8aCTKN78WyooJddFQgLnCy0xKqcDU0"
      />

      <title>{seoData.seo_content_title}</title>

      <script
        type="application/ld+json"
        className="yoast-schema-graph"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "WebPage",
                "@id": `https://aghomedecor.in${location.pathname}`,
                url: `https://aghomedecor.in${location.pathname}`,
                name: seoData.seo_content_title,
                isPartOf: {
                  "@id": "https://aghomedecor.in/#website",
                },
                description: seoData.seo_content_description,
                breadcrumb: {
                  "@id": "https://aghomedecor.in/#breadcrumb",
                },
                inLanguage: "en-IN",
                potentialAction: [
                  {
                    "@type": "ReadAction",
                    target: [`https://aghomedecor.in${location.pathname}`],
                  },
                ],
              },
              {
                "@type": "BreadcrumbList",
                "@id": "https://aghomedecor.in/#breadcrumb",
                itemListElement: [
                  {
                    "@type": "ListItem",
                    position: 1,
                    name: "Home",
                    item: "https://aghomedecor.in/",
                  },
                ],
              },
              {
                "@type": "WebSite",
                "@id": "https://aghomedecor.in/#website",
                url: "https://aghomedecor.in/",
                name: "AG Home Decor",
                description: "Premium home decor and interior styling products",
                potentialAction: [
                  {
                    "@type": "SearchAction",
                    target: {
                      "@type": "EntryPoint",
                      urlTemplate:
                        "https://aghomedecor.in/?s={search_term_string}",
                    },
                    "query-input": "required name=search_term_string",
                  },
                ],
                inLanguage: "en-IN",
              },
            ],
          }),
        }}
      />
      <meta name="description" content={seoData.seo_content_description} />
      <meta name="keywords" content={seoData.seo_content_keyword} />
      <meta name="author" content={seoData.seo_content_author} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={seoData.seo_content_title} />
      <meta
        property="og:description"
        content={seoData.seo_content_description}
      />
      <meta property="og:image" content="https://aghomedecor.in/AGLOGO.png" />
      <meta
        property="og:url"
        content={`https://aghomedecor.in${location.pathname}`}
      />
      <meta property="og:site_name" content="AG Home Decor" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoData.seo_content_title} />
      <meta
        name="twitter:description"
        content={seoData.seo_content_description}
      />
      <meta name="twitter:image" content="https://aghomedecor.in/AGLOGO.png" />
      <meta name="twitter:site" content="@AGHomeDecor" />
      <meta name="twitter:creator" content="AG Home Decor" />

      {/* Additional Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="robots" content="index, follow" />

      {/* Canonical URL */}
      <link
        rel="canonical"
        href={`https://aghomedecor.in${location.pathname}`}
      />
    </Helmet>
  );
};

export default DynamicSeo;
